import React, { useEffect, useState } from 'react';
import Modal from './Modal';
import { apiFetch } from '../lib/api';
import { Sede, SocietaSederischio, AllegatoPolizza } from '../types';
import BACKEND_URL from '../config.js';

// Tipo minimale per la prop societa (il wizard usa solo id e nome)
type SocietaItem = { id: number; nome: string };

// ── Tipi ──────────────────────────────────────────────────────────────────
export type WizardForm = {
  societaId:      string;
  sedeIds:        number[];
  rischiMassimali: Record<number, string>; // societaSederischioId -> massimale
  numero:         string;
  compagnia:      string;
  agente:         string;
  dataInizio:     string;
  dataFine:       string;
  tacitaRinnovo:  boolean;
  importoTotale:  string;
  note:           string;
};

export const EMPTY_WIZARD: WizardForm = {
  societaId: '', sedeIds: [], rischiMassimali: {},
  numero: '', compagnia: '', agente: '',
  dataInizio: '', dataFine: '', tacitaRinnovo: false,
  importoTotale: '', note: '',
};

interface Props {
  mode:              'create' | 'edit';
  polizzaId?:        number;
  initial?:          WizardForm;
  existingAllegati?: AllegatoPolizza[];
  societa:           SocietaItem[];
  sediAll:           Sede[];
  onSaved:           () => void;
  onClose:           () => void;
}

export default function PolizzaWizard({
  mode, polizzaId, initial, existingAllegati = [],
  societa, sediAll, onSaved, onClose,
}: Props) {
  const [step,   setStep]   = useState<1 | 2 | 3>(1);
  const [form,   setForm]   = useState<WizardForm>(initial ?? { ...EMPTY_WIZARD });
  const [assoc,  setAssoc]  = useState<SocietaSederischio[]>([]);
  const [newFiles,       setNewFiles]       = useState<File[]>([]);
  const [deleteAllegati, setDeleteAllegati] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const sediPerSocieta = sediAll.filter(
    (s) => !form.societaId || s.societaId === Number(form.societaId)
  );

  // Carica associazioni quando la società cambia (step 1 → 2)
  const loadAssoc = async (socId: string) => {
    if (!socId) { setAssoc([]); return; }
    try {
      const d = await apiFetch<{ associazioni: SocietaSederischio[] }>(
        `/api/assicurazioni/associazioni?societaId=${socId}`
      );
      setAssoc(d.associazioni);
    } catch (_) {}
  };

  // In edit mode, carica le associazioni al mount
  useEffect(() => {
    if (mode === 'edit' && initial?.societaId) {
      loadAssoc(initial.societaId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helpers ──────────────────────────────────────────────────────────────
  const toggleSede = (id: number) => {
    setForm((prev) => ({
      ...prev,
      sedeIds: prev.sedeIds.includes(id)
        ? prev.sedeIds.filter((s) => s !== id)
        : [...prev.sedeIds, id],
    }));
  };

  // Per una data sede, restituisce le associazioni da mostrare (specifiche + generali)
  const assocForSede = (sedeId: number): SocietaSederischio[] => {
    const specific = assoc.filter((a) => a.sedeId === sedeId);
    const global   = assoc.filter((a) => a.sedeId === null);
    // Deduplica per rischioId
    const seen = new Set<number>();
    return [...specific, ...global].filter((a) => {
      if (seen.has(a.rischioId!)) return false;
      seen.add(a.rischioId!);
      return true;
    });
  };

  // Tutte le associazioni uniche visibili in step 2
  const allVisibleAssoc = (): SocietaSederischio[] => {
    const byId = new Map<number, SocietaSederischio>();
    for (const sedeId of form.sedeIds) {
      for (const a of assocForSede(sedeId)) {
        byId.set(a.id, a);
      }
    }
    return Array.from(byId.values());
  };

  // ── Navigazione wizard ────────────────────────────────────────────────────
  const goToStep2 = async () => {
    if (!form.societaId) { setError('Selezionare una società'); return; }
    if (form.sedeIds.length === 0) { setError('Selezionare almeno una sede'); return; }
    setError('');
    await loadAssoc(form.societaId);
    setStep(2);
  };

  const goToStep3 = () => { setError(''); setStep(3); };
  const backTo1   = () => { setError(''); setStep(1); };
  const backTo2   = () => { setError(''); setStep(2); };

  const handleSocietaChange = (val: string) => {
    setForm((prev) => ({ ...prev, societaId: val, sedeIds: [] }));
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!form.numero || !form.compagnia || !form.dataInizio || !form.dataFine) {
      setError('Compilare tutti i campi obbligatori'); return;
    }
    setSaving(true); setError('');

    const rischiPayload = allVisibleAssoc().map((a) => ({
      societaSederischioId: a.id,
      massimale: form.rischiMassimali[a.id] ? Number(form.rischiMassimali[a.id]) : null,
    }));

    const fd = new FormData();
    fd.append('societaId',      form.societaId);
    fd.append('sedeIds',        JSON.stringify(form.sedeIds));
    fd.append('rischi',         JSON.stringify(rischiPayload));
    fd.append('numero',         form.numero);
    fd.append('compagnia',      form.compagnia);
    fd.append('agente',         form.agente);
    fd.append('dataInizio',     form.dataInizio);
    fd.append('dataFine',       form.dataFine);
    fd.append('tacitaRinnovo',  String(form.tacitaRinnovo));
    fd.append('importoTotale',  form.importoTotale);
    fd.append('note',           form.note);
    if (mode === 'edit') {
      fd.append('deleteAllegatiIds', JSON.stringify(deleteAllegati));
    }
    newFiles.forEach((f) => fd.append('allegati', f));

    const token = localStorage.getItem('token');
    const url   = mode === 'create'
      ? `${BACKEND_URL}/api/assicurazioni/polizze`
      : `${BACKEND_URL}/api/assicurazioni/polizze/${polizzaId}`;

    try {
      const res = await fetch(url, {
        method:  mode === 'create' ? 'POST' : 'PUT',
        headers: { Authorization: `Bearer ${token || ''}` },
        body:    fd,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error || `Errore ${res.status}`);
      }
      onSaved();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Allegati esistenti ────────────────────────────────────────────────────
  const toggleDeleteAllegato = (id: number) => {
    setDeleteAllegati((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // ── Titoli e footer per step ──────────────────────────────────────────────
  const titles = ['Seleziona società e sedi', 'Rischi per sede', 'Dati polizza'];
  const title  = `${mode === 'create' ? 'Nuova polizza' : 'Modifica polizza'} — ${step}/3: ${titles[step - 1]}`;

  const footer = (
    <div className="bop-polizza-wiz__footer">
      <div className="bop-polizza-wiz__footer-left">
        {step > 1 && (
          <button className="bop-btn-secondary"
            onClick={step === 2 ? backTo1 : backTo2}>
            ← Indietro
          </button>
        )}
      </div>
      <div className="bop-polizza-wiz__footer-right">
        <button className="bop-btn-secondary" onClick={onClose}>Annulla</button>
        {step < 3 && (
          <button className="bop-btn-primary"
            onClick={step === 1 ? goToStep2 : goToStep3}>
            Avanti →
          </button>
        )}
        {step === 3 && (
          <button className="bop-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvo…' : 'Salva polizza'}
          </button>
        )}
      </div>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Modal title={title} onClose={onClose} footer={footer}>
      {error && <div className="inline-error">{error}</div>}

      {/* ── STEP 1: Società e sedi ─────────────────────────────────────── */}
      {step === 1 && (
        <>
          <div className="bop-form-group">
            <label className="bop-label">Società <span className="req">*</span></label>
            <select className="bop-input" value={form.societaId}
              onChange={(e) => handleSocietaChange(e.target.value)}>
              <option value="">— Seleziona —</option>
              {societa.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
            </select>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Sedi coperte <span className="req">*</span></label>
            <div className="bop-polizza-wiz__checkbox-list">
              {!form.societaId && (
                <span className="bop-polizza-wiz__hint">Seleziona prima una società</span>
              )}
              {form.societaId && sediPerSocieta.length === 0 && (
                <span className="bop-polizza-wiz__hint">Nessuna sede disponibile</span>
              )}
              {sediPerSocieta.map((s) => (
                <label key={s.id} className="bop-polizza-wiz__checkbox-item">
                  <input type="checkbox" checked={form.sedeIds.includes(s.id)}
                    onChange={() => toggleSede(s.id)} />
                  {s.nome}{s.citta ? ` — ${s.citta}` : ''}
                </label>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ── STEP 2: Rischi per sede ────────────────────────────────────── */}
      {step === 2 && (
        <>
          <p className="bop-polizza-wiz__step2-intro">
            Per ogni sede inserisci i massimali (opzionali). Tutti i rischi associati verranno inclusi nella polizza.
          </p>
          {form.sedeIds.map((sedeId) => {
            const sede   = sediAll.find((s) => s.id === sedeId);
            const rischi = assocForSede(sedeId);
            return (
              <div key={sedeId} className="bop-polizza-wiz__sede-block">
                <div className="bop-polizza-wiz__sede-header">
                  {sede?.nome || `Sede ${sedeId}`}
                </div>
                {rischi.length === 0 ? (
                  <div className="bop-polizza-wiz__sede-empty">
                    Nessun rischio associato a questa sede.
                  </div>
                ) : (
                  <table className="bop-polizza-wiz__rischi-table">
                    <thead>
                      <tr>
                        <th className="bop-polizza-wiz__rischi-th">Rischio</th>
                        <th className="bop-polizza-wiz__rischi-th bop-polizza-wiz__rischi-th--massimale">Massimale (€)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rischi.map((a) => (
                        <tr key={a.id} className="bop-polizza-wiz__rischi-row">
                          <td className="bop-polizza-wiz__rischi-td">
                            {a.rischio_nome}
                            {a.sedeId === null && (
                              <span className="bop-polizza-wiz__tag-generale">(generale)</span>
                            )}
                          </td>
                          <td className="bop-polizza-wiz__rischi-td--input">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              className="bop-input bop-polizza-wiz__massimale-input"
                              value={form.rischiMassimali[a.id] ?? ''}
                              onChange={(e) => setForm((prev) => ({
                                ...prev,
                                rischiMassimali: { ...prev.rischiMassimali, [a.id]: e.target.value },
                              }))}
                              placeholder="es. 500000"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </>
      )}

      {/* ── STEP 3: Dati polizza ───────────────────────────────────────── */}
      {step === 3 && (
        <>
          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Numero polizza <span className="req">*</span></label>
              <input className="bop-input" value={form.numero}
                onChange={(e) => setForm({ ...form, numero: e.target.value })} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Compagnia <span className="req">*</span></label>
              <input className="bop-input" value={form.compagnia}
                onChange={(e) => setForm({ ...form, compagnia: e.target.value })} />
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Agente assicurativo</label>
            <input className="bop-input" value={form.agente}
              onChange={(e) => setForm({ ...form, agente: e.target.value })} />
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Data inizio <span className="req">*</span></label>
              <input className="bop-input" type="date" value={form.dataInizio}
                onChange={(e) => setForm({ ...form, dataInizio: e.target.value })} />
            </div>
            <div className="bop-form-group">
              <label className="bop-label">Data scadenza <span className="req">*</span></label>
              <input className="bop-input" type="date" value={form.dataFine}
                onChange={(e) => setForm({ ...form, dataFine: e.target.value })} />
            </div>
          </div>

          <div className="bop-form-row">
            <div className="bop-form-group">
              <label className="bop-label">Importo totale polizza (€)</label>
              <input className="bop-input" type="number" step="0.01" min="0"
                value={form.importoTotale}
                onChange={(e) => setForm({ ...form, importoTotale: e.target.value })} />
            </div>
            <div className="bop-form-group bop-polizza-wiz__checkbox-bottom">
              <label className="bop-polizza-wiz__checkbox-item">
                <input type="checkbox" checked={form.tacitaRinnovo}
                  onChange={(e) => setForm({ ...form, tacitaRinnovo: e.target.checked })} />
                Tacito rinnovo
              </label>
            </div>
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Note</label>
            <textarea className="bop-input bop-polizza-wiz__textarea" rows={3} value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })} />
          </div>

          {/* Allegati esistenti (solo in edit) */}
          {mode === 'edit' && existingAllegati.length > 0 && (
            <div className="bop-form-group">
              <label className="bop-label">Allegati esistenti</label>
              <div className="bop-polizza-wiz__checkbox-list">
                {existingAllegati.map((a) => {
                  const markedForDelete = deleteAllegati.includes(a.id);
                  return (
                    <div key={a.id} className={`bop-polizza-wiz__allegato-row ${markedForDelete ? 'bop-polizza-wiz__allegato-row--deleted' : ''}`}>
                      <span className="bop-polizza-wiz__allegato-name">{a.nome}</span>
                      <button type="button" className={`bop-btn-ghost bop-polizza-wiz__allegato-action ${markedForDelete ? 'bop-polizza-wiz__allegato-action--restore' : 'bop-polizza-wiz__allegato-action--remove'}`}
                        onClick={() => toggleDeleteAllegato(a.id)}>
                        {markedForDelete ? 'Ripristina' : '✕ Rimuovi'}
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Upload nuovi allegati */}
          <div className="bop-form-group">
            <label className="bop-label">{mode === 'edit' ? 'Aggiungi nuovi allegati' : 'Allegati'}</label>
            <input
              type="file"
              className="bop-input"
              multiple
              onChange={(e) => setNewFiles(Array.from(e.target.files || []))}
            />
            {newFiles.length > 0 && (
              <span className="bop-polizza-wiz__file-count">
                {newFiles.length} file selezionati
              </span>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
